/**
 * Forge Dashboard Service
 * 
 * API client for fetching Forge system health, resources, and dashboard statistics.
 * These endpoints are restricted to super-admin users only.
 * 
 * Uses a single combined endpoint that handles errors gracefully and returns
 * partial data with warnings instead of failing completely.
 */
import apiClient from './apiClient';
import {
  ForgeSystemHealth,
  ForgeSystemResources,
  ForgeResourcesUsage,
  ForgeDashboardStats,
  ForgeDashboardResponse,
  ComponentError,
} from '../types';

const apiBaseURL = '/forge/admin';

// =============================================================================
// Individual endpoints (kept for backwards compatibility)
// =============================================================================

/**
 * Get system health status for all Forge components.
 * Returns health status for: backoffice, traefik, mongodb, argocd, tekton.
 * Returns default values with error status if fetch fails.
 * 
 * Requires super-admin permissions.
 */
export const getSystemHealth = async (): Promise<ForgeSystemHealth> => {
  const response = await apiClient.get<ForgeSystemHealth>(`${apiBaseURL}/system/health`);
  return response.data;
};

/**
 * Get cluster resource capacity (CPU, Memory, Pods).
 * Returns default values with error status if fetch fails.
 * 
 * Requires super-admin permissions.
 */
export const getSystemResources = async (): Promise<ForgeSystemResources> => {
  const response = await apiClient.get<ForgeSystemResources>(`${apiBaseURL}/system/resources`);
  return response.data;
};

/**
 * Get current resource usage metrics.
 * Returns default values with error status if fetch fails.
 * 
 * Requires super-admin permissions.
 */
export const getResourcesUsage = async (): Promise<ForgeResourcesUsage> => {
  const response = await apiClient.get<ForgeResourcesUsage>(`${apiBaseURL}/system/resources/usage`);
  return response.data;
};

/**
 * Get aggregated statistics for the Forge Dashboard.
 * Returns default values with error flag if fetch fails.
 * 
 * Requires super-admin permissions.
 */
export const getDashboardStats = async (): Promise<ForgeDashboardStats> => {
  const response = await apiClient.get<ForgeDashboardStats>(`${apiBaseURL}/dashboard-stats`);
  return response.data;
};

// =============================================================================
// Combined endpoint (recommended - handles errors gracefully)
// =============================================================================

/**
 * Fetch all dashboard data in a single request with graceful error handling.
 * 
 * This is the recommended way to fetch dashboard data. It:
 * - Fetches all components in parallel on the backend
 * - Returns partial data if some components fail
 * - Includes a list of errors for displaying warnings
 * - Never throws HTTP errors to the frontend
 * 
 * @returns ForgeDashboardResponse with status, data, and errors list
 * 
 * Requires super-admin permissions.
 */
export const getDashboard = async (): Promise<ForgeDashboardResponse> => {
  const response = await apiClient.get<ForgeDashboardResponse>(`${apiBaseURL}/dashboard`);
  return response.data;
};

// =============================================================================
// Global Forge API kill-switch
// =============================================================================

export interface ForgeApiSwitch {
  enabled: boolean;
}

/** Whether the backend is currently allowed to call the Forge API. */
export const getApiSwitch = async (): Promise<ForgeApiSwitch> => {
  const response = await apiClient.get<ForgeApiSwitch>(`${apiBaseURL}/api-switch`);
  return response.data;
};

/**
 * Turn all backend calls to the Forge API on or off.
 * While off, every Forge endpoint answers 503 without contacting Forge.
 *
 * Requires super-admin permissions.
 */
export const setApiSwitch = async (enabled: boolean): Promise<ForgeApiSwitch> => {
  const response = await apiClient.put<ForgeApiSwitch>(`${apiBaseURL}/api-switch`, { enabled });
  return response.data;
};

// =============================================================================
// Legacy interface for backwards compatibility
// =============================================================================

export interface ForgeDashboardData {
  health: ForgeSystemHealth;
  resources: ForgeSystemResources;
  usage: ForgeResourcesUsage;
  stats: ForgeDashboardStats;
  errors?: ComponentError[];
  status?: 'success' | 'partial' | 'error';
}

/**
 * Fetch all dashboard data using the combined endpoint.
 * This is now a wrapper around getDashboard for backwards compatibility.
 * 
 * Requires super-admin permissions.
 */
export const getAllDashboardData = async (): Promise<ForgeDashboardData> => {
  const response = await getDashboard();
  return {
    health: response.health,
    resources: response.resources,
    usage: response.usage,
    stats: response.stats,
    errors: response.errors,
    status: response.status,
  };
};

// =============================================================================
// Default export as service object
// =============================================================================

const forgeDashboardService = {
  // Individual endpoints
  getSystemHealth,
  getSystemResources,
  getResourcesUsage,
  getDashboardStats,
  
  // Combined endpoint (recommended)
  getDashboard,
  
  // Legacy combined fetch
  getAllDashboardData,

  // Kill-switch
  getApiSwitch,
  setApiSwitch,
};

export default forgeDashboardService;
